import type { ImportRunStatus } from "../importRun/importRunValues";
import type { ListColumn } from "../../../../../../../shared/frontend/list/listColumn";
import type { ColumnsType } from "../../../../../../../shared/frontend/ui";
import { JSON_INDENT } from "../../../app/constants/jsonText";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";

/** Antwort des Rahmens beim Start eines Laufjobs (Upload, Analyse, Vorprüfung, Import). */
export interface ImportAdapterJobStartDto {
  success: boolean;
  /** Fehlerschlüssel aus `ApiErrorKeys`, nur bei `success: false`. */
  errorKey?: string | null;
  failureCode?: "already-committed" | "already-running" | "precheck-required" | null;
  jobId?: string | null;
  jobType?: string | null;
  title?: string | null;
}

export interface ImportAdapterRollbackResponseDto {
  success: boolean;
  errors?: string[];
  jobId?: string | null;
  backupId?: string | null;
}

export interface ImportAdapterRunHeadDto {
  id: string;
  adapterId: string;
  tenantId: string;
  status: ImportRunStatus;
}

/** Zeile der Laufliste — für jeden Adapter dieselbe Form (`ImportAdapterRunListRow` im Backend). */
export interface ImportAdapterRunListRow {
  id: string;
  tenantId: string;
  tenantName?: string | null;
  displayName?: string | null;
  adapterId: string;
  status: ImportRunStatus;
  importMode?: string | null;
  previousImportRunId?: string | null;
  createdAt: string;
  updatedAt: string;
  uploadSummary?: { fileCount: number; itemCount: number } | null;
  rollbackAvailable: boolean;
}

/** Payload des Realtime-Ereignisses `ImportAdapterProgress`. */
export interface ImportAdapterProgressPayload {
  adapterId?: string | null;
  runId?: string | null;
  phase?: string | null;
  current?: number | null;
  total?: number | null;
  message?: string | null;
}

/** Zielwert einer Zuordnung, der die Quelle bewusst nicht importiert. */
export const ignoreImportTargetValue = "__ignore__";
/** Zielwert einer Zuordnung, der ein neues Ziel anlegt. */
export const newImportTargetValue = "__new__";

/** Importlauf-Status in Kleinschreibung (Vergleichswerte nach toLowerCase()). */
export const IMPORT_ADAPTER_STATUS = {
  Draft: "draft",
  Ready: "ready",
  Uploaded: "uploaded",
  UploadProcessing: "uploadprocessing",
  ClearingUploads: "clearinguploads",
  Prechecking: "prechecking",
  PrecheckFailed: "precheckfailed",
  Committing: "committing",
  Committed: "committed",
  Failed: "failed",
  RolledBack: "rolledback",
} as const;

export function importAdapterStatusColor(status?: string | null) {
  const value = String(status ?? "").trim().toLowerCase();
  if (value === IMPORT_ADAPTER_STATUS.Committed || value === IMPORT_ADAPTER_STATUS.Ready) return "green";
  if (value === IMPORT_ADAPTER_STATUS.Committing || value === IMPORT_ADAPTER_STATUS.Prechecking || value === IMPORT_ADAPTER_STATUS.UploadProcessing || value === IMPORT_ADAPTER_STATUS.ClearingUploads) return "processing";
  if (value === IMPORT_ADAPTER_STATUS.PrecheckFailed || value === IMPORT_ADAPTER_STATUS.Failed) return "red";
  if (value === IMPORT_ADAPTER_STATUS.Uploaded) return "orange";
  if (value === IMPORT_ADAPTER_STATUS.RolledBack) return "volcano";
  return "blue";
}

export function replacePlaceholders(template: string, values: Array<string | number | null | undefined>): string {
  return values.reduce<string>(
    (acc, value, index) => acc.replaceAll(`{${index}}`, String(value ?? "")),
    template
  );
}

export function translateJobMessage(message: string | null | undefined, text: (key: string) => string) {
  const raw = (message ?? "").trim();
  if (!raw) return "";
  const packages = /^Beitragskalender: (\d+)\/(\d+) Vertragspakete$/.exec(raw);
  if (packages) return replacePlaceholders(text("Import maintenance package progress"), [packages[1], packages[2]]);
  return raw.startsWith("tenantBackup.") ? text(raw) : raw;
}

export function compareImportText(a: unknown, b: unknown) {
  return String(a ?? "").trim().localeCompare(String(b ?? "").trim(), undefined, {
    numeric: true,
    sensitivity: "base"
  });
}

export function buildColumnEditorConfig<T extends object>(columns: ListColumn<T>[]) {
  const entries = columns
    .map((column) => ({ key: String(column.key ?? ""), column }))
    .filter((entry) => entry.key.length > 0);
  return {
    definitions: entries.map((entry) => ({ key: entry.key, defaultVisible: true })),
    columnsByKey: Object.fromEntries(entries.map((entry) => [entry.key, entry.column])) as Record<string, ListColumn<T>>
  };
}

/** Breite der Bereichsauswahl im Zuordnungsschritt: am längsten Eintrag ausgerichtet, innerhalb fester Grenzen. */
const DECISION_AREA_SELECT_MAX_WIDTH = 760;
const DECISION_AREA_SELECT_MIN_WIDTH = 320;
const DECISION_AREA_SELECT_CHAR_WIDTH = 8;
const DECISION_AREA_SELECT_PADDING = 56;

export function decisionAreaSelectWidthFor(items: Array<{ label: string }>) {
  return Math.min(DECISION_AREA_SELECT_MAX_WIDTH, Math.max(DECISION_AREA_SELECT_MIN_WIDTH, ...items.map((item) => item.label.length * DECISION_AREA_SELECT_CHAR_WIDTH + DECISION_AREA_SELECT_PADDING)));
}

const CONFIGURATION_FILE_NAME_MAX_LENGTH = 80;
const DOWNLOAD_URL_REVOKE_DELAY_MS = 30_000;

export function sanitizeConfigurationFileName(value: string) {
  const cleaned = value
    .trim()
    .replace(/[^a-z0-9._-]+/gi, "-")
    .replace(/^-+|-+$/g, "");
  return (cleaned || "import").slice(0, CONFIGURATION_FILE_NAME_MAX_LENGTH);
}

export function buildAdapterConfigurationFileName(label: string | null | undefined, fallback = "import") {
  const safe = sanitizeConfigurationFileName(label || fallback);
  const date = new Date().toISOString().slice(0, ISO_DATE_LENGTH);
  return `${safe}-configuration-${date}.json`;
}

export function downloadJsonFile(fileName: string, payload: unknown) {
  const blob = new Blob([JSON.stringify(payload ?? {}, null, JSON_INDENT)], { type: "application/json" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), DOWNLOAD_URL_REVOKE_DELAY_MS);
}

export type DecisionTableColumns<T extends object> = ColumnsType<T> | ListColumn<T>[];

export function toDecisionTableColumns<T extends object>(columns: DecisionTableColumns<T>): ColumnsType<T> {
  return columns as ColumnsType<T>;
}
